import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import Image from 'next/image'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import {loadImageFromKontentAI} from '@/image-loader'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {Faq} from '../faq'
import {FaqItem} from '../faq-item'
import type {CtaButtonContentItem} from '../cta-button'
import {ReusableCTAButtonComponent} from '../cta-button'
import type {VideoBlockContentItem} from '../videos/video-block'
import {VideoBlockBlock as YouTubeBlock} from '../videos/video-block'
import type {WistiaContentItem} from '../videos/wistia'
import {WistiaBlock} from '../videos/wistia'

export type FaqItemContentItem = IContentItem<{
	title: Elements.TextElement
	content: Elements.RichTextElement
	imageUrl: Elements.AssetsElement
	faqItemButtons: Elements.LinkedItemsElement<CtaButtonContentItem>
	config: Elements.LinkedItemsElement // TODO: restrict to FAQ config
	video: Elements.LinkedItemsElement<
		WistiaContentItem | VideoBlockContentItem
	>
	fullWidthImage: Elements.MultipleChoiceElement
}>

export type FaqContentItem = IContentItem<{
	faqItems: Elements.LinkedItemsElement<FaqItemContentItem>
	/** @deprecated Use expandIconColorPicker instead */
	expandIconColor: Elements.MultipleChoiceElement
	/** New color picker field - takes priority over expandIconColor */
	expandIconColorPicker: Elements.CustomElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
}>

export const FaqBlock: Block<FaqContentItem> = ({block, page, ...context}) => {
	const componentSpacing =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary as snippetSpacingSpacing may be undefined
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		] || spacing.none

	// Color priority: expandIconColorPicker (new picker) → expandIconColor (deprecated dropdown) → page config → black
	const {hexCode} = colorWithFallback({
		blockColor: block.elements.expandIconColorPicker,
		blockColorLegacy: block.elements.expandIconColor,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'black',
		fallbackHexColor: '#000000',
	})
	// iconColor always has a value due to fallbackHexColor
	const iconColor = hexCode ?? '#000000'

	return (
		<div className={componentSpacing}>
			<Faq
				className="container-narrow faq-block"
				data-kontent-element-codename="faq_items"
			>
				{block.elements.faqItems.map((item) => (
					<FaqItem
						data-kontent-element-codename="faq_item"
						iconColor={iconColor}
						key={item.system.id}
						title={item.elements.title}
					>
						<div
							className={clsx(
								'mt-6',
								item.elements.fullWidthImage[0]?.codename ===
									'yes'
									? 'flex-col'
									: 'flex'
							)}
						>
							{item.elements.imageUrl[0] ? (
								<div
									className={clsx(
										'overflow-hidden',
										item.elements.fullWidthImage[0]
											?.codename === 'yes'
											? 'mb-5 w-full'
											: 'w-1/2'
									)}
								>
									<Image
										alt=""
										className="pr-2"
										data-kontent-element-codename="image_url"
										height={
											item.elements.imageUrl[0].height ||
											200
										}
										loader={loadImageFromKontentAI}
										src={item.elements.imageUrl[0].url}
										width={
											item.elements.imageUrl[0].width ||
											200
										}
									/>
								</div>
							) : null}
							<RichTextRenderer
								className={clsx(
									'content faq-content text-base text-black',
									item.elements.imageUrl[0]
										? 'w-1/2 pl-2'
										: ''
								)}
								element={item.elements.content}
								page={page}
								{...context}
							/>
						</div>
						{item.elements.faqItemButtons.map((button) => (
							<ReusableCTAButtonComponent
								block={button}
								className="m-auto mr-6 mt-6"
								key={button.system.id}
								page={page}
								{...context}
							/>
						))}
						{item.elements.video.map((v) =>
							v.system.type === 'video_block' ? (
								<YouTubeBlock
									block={v as Tersed<VideoBlockContentItem>} // Assertion is correct as it is narrowed by system.type
									key={v.system.id}
									page={page}
									{...context}
								/>
							) : (
								<WistiaBlock
									block={v as Tersed<WistiaContentItem>} // Assertion is correct as it is narrowed by system.type
									key={v.system.id}
									page={page}
									{...context}
								/>
							)
						)}
					</FaqItem>
				))}
			</Faq>
		</div>
	)
}
